// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import { formatDate } from "services/utility/dates";
import {
  extractName,
  formatDocumentInfo,
  formatStatus,
  formatDateTimeToDate,
  formatNumberLinkField,
  truncate,
  formatBooleanToYesOrBlank,
  formatQuantityWithUom,
  extractDescription,
  formatLinkableItem,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { TYPES, starColumn } from "services/utility/schema";

import { DEFAULT_DECIMALS_UNROUNDED, NO_REF_NUMBER_STRING } from "appConstants";

export function getEmptyRecord() {
  return {
    starred: 0,
    syncToken: 0,
    number: "",
    date: new Date(),
    customer: null,
    location: null,
    channel: null,
    priority: null,
    assignedToUser: null,
    shippingMethod: null,
    trackingNumber: null,
    linkedTransaction: null,
    comment: null,
    customerNotes: null,
    closed: false,
    customFields: [],
    pickBy: null,
    total: Decimal.ZERO,
    archived: false,
    summaryOnly: false,
    hasSignature: false,
    documents: [],
    keys: null,
    values: null,
    lines: [],
  };
}

export const LINE_ITEM_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  description: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  class: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  volumeunit: { type: TYPES.text },
  linkedTransaction: { type: TYPES.linkedTransaction },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  uom: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  lot: { type: TYPES.reference },
  shipped: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  picked: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  invoiced: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  returned: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  produced: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  onhand: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: {
    initialValue: {},
  },
};

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDateTimeToDate,
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
    default: true,
  },
  {
    name: "status",
    fieldName: "closed",
    formatFunc: formatStatus,
    heading: i18n("columns.Status"),
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    formatFunc: formatDocumentInfo,
    minWidth: "15em",
    default: true,
  },
  {
    name: "customer",
    heading: i18n("columns.Customer"),
    formatFunc: extractName,
    minWidth: "10em",
    maxWidth: "25em",
    sortable: true,
  },
  {
    name: "pickBy",
    heading: i18n("columns.PickBy"),
    formatFunc: formatDate,
    sortable: true,
  },
  {
    name: "priority",
    heading: i18n("columns.Priority"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "assignedToUser",
    heading: i18n("columns.AssignedTo"),
    formatFunc: extractName,
    minWidth: "10em",
    sortable: true,
  },
  {
    name: "channel",
    heading: i18n("columns.Channel"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "customerNotes",
    heading: i18n("columns.CustomerNotes"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

const columnArray = Object.entries(columnInfo);
const defaultColumnInfo = columnArray.filter((col) => col[1].required);
export const defaultColumns = defaultColumnInfo.map((col) => col[0]);

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: [],
  MONEY_FIELDS: ["total"],
  DATE_FIELDS: ["pickBy"],
  DATETIME_FIELDS: ["date"],
  NON_UTC_DATETIME_FIELDS: [],
};

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "shipment",
  "archive",
  "openClose",
  "delete",
  "history",
];
export const BATCH_ACTIONS = [
  ["pdf", "email"],
  "delete",
  "archive",
  "openClose",
];
export const REFERENCE_FIELDS = ["location", "customer"];

export const ITEM_CALCULATED_FIELDS = ["onhand", "locationBins"];
export const ITEM_CALCULATED_FIELDS_NO_ON_HAND = ["locationBins"];

export const QUICK_VIEW_CONFIG = {
  titleSetting: "pickTicketTitle",
  footerSetting: "pickTicketFooter",
  signatureLinesLabel: i18n("colon.PulledBy"),
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.PickTicketNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
  ],
};
